import { Show } from 'solid-js';
import { Button } from '@bz/ui';
import { IconRotateLeft } from '@bz/icons/index.js';
import { createWritableMemo } from '@solid-primitives/memo';

import { customerServiceManager } from '~/components/Chat/customerServiceManager';

type RiskLevel = 'low' | 'high';

interface AssessmentResult {
  level: RiskLevel;
  title: string;
  description: string;
  recommendation: string;
  bgColor: string;
  primaryButton: string;
}

export const route = {
  info: {
    login: true,
  },
} satisfies RouteDefinition;
export default function SelfAssessment() {
  const navigate = useNavigate();
  const limits = createAsync(() => get.getUserInsuranceLastResult().catch());
  const riskLevel = createMemo(() => {
    return (limits()?.type ?? 'low') as RiskLevel;
  });
  const [showResult, setShowResult] = createWritableMemo(() => !!limits());
  const results: Record<RiskLevel, AssessmentResult> = {
    low: {
      level: 'low',
      title: t('Low Risk'),
      description: t('Based on your responses, your gambling habits currently indicate a low risk level.'),
      recommendation: t(
        'We encourage you to continue playing responsibly and staying in control of your gameplay. Remember, you can always use our Limit Settings to help maintain this balance.'
      ),
      bgColor: 'bg-alpha-brand-brand-a10',
      primaryButton: t('Back to Home'),
    },
    high: {
      level: 'high',
      title: t('High Risk'),
      description: t(
        'Based on your responses, your current gambling habits indicate a high risk and may be negatively impacting your daily life.'
      ),
      recommendation: t(
        'We strongly recommend seeking professional support. Please consider using our self-exclusion tools and reaching out to dedicated problem gambling support services.'
      ),
      bgColor: 'bg-alpha-function-red-a10',
      primaryButton: t('Contact Support'),
    },
  };

  const currentResult = createMemo(() => results[riskLevel()]);

  const handleTestAgain = () => {
    setShowResult(false);
  };

  const handlePrimaryAction = () => {
    if (riskLevel() === 'low') {
      navigate('/responsible/overview');
    } else {
      customerServiceManager.open();
    }
  };

  return (
    <div class=" flex flex-col gap-4 p-4 rounded-xl shadow-[0px_2px_24px_0px_rgba(10,15,19,0.05)] w-full">
      <Show when={!showResult()}>
        {/* 初始页面 */}
        <div class="bg-layer3-card border border-main2-auto flex flex-col gap-3 px-4 py-4 rounded-lg shadow-[0px_2px_16px_0px_rgba(10,15,19,0.05)] w-full">
          <div class="flex flex-col gap-1 w-full">
            <div class="font-semibold text-base text-neutral-main1 leading-[1.5]">{t('Gambling Self-Assessment')}</div>
            <p class="font-semibold text-sm text-neutral-main3 leading-[1.5]">
              {t(
                'Answer the following questions to assess your gambling habits. For each question, select how often the statement applies to you.'
              )}
            </p>
          </div>
          <div class="flex items-center w-full">
            <A href="/responsible/assessment-qa">
              <Button type="brand" class="h-11 min-w-[200px] w-[200px] rounded-full">
                {t('Start Self-Assessment')}
              </Button>
            </A>
          </div>
        </div>
      </Show>

      <Show when={showResult()}>
        {/* 结果页面 */}
        <div class="flex flex-col gap-4 w-full">
          {/* 进度条 */}
          <div class="flex flex-col gap-4 w-full">
            <div class="flex items-center justify-between w-full">
              <div class="flex gap-1 items-center">
                <IconRotateLeft class="size-4" />
                <p class="font-semibold text-sm text-neutral-main1 leading-[1.2] whitespace-nowrap">{t('Question')}</p>
              </div>
              <p class="font-semibold text-sm text-neutral-main2 leading-[1.2] text-right whitespace-nowrap">10/10</p>
            </div>
            <div class="flex flex-col w-full">
              <div class="bg-layer3 h-2.5 overflow-hidden w-full relative rounded">
                <div class="absolute bg-brand h-2.5 left-0 rounded-full top-0 w-full" />
              </div>
            </div>
          </div>

          {/* 结果卡片 */}
          <div class="bg-layer3-card border border-main2-auto flex flex-col gap-3 px-4 py-4 rounded-lg shadow-[0px_2px_16px_0px_rgba(10,15,19,0.05)] w-full">
            <div class="flex flex-col gap-2 w-full">
              <div class="font-semibold text-base text-neutral-main1 leading-[1.5]">{currentResult().title}</div>
              <p class="font-semibold text-sm text-neutral-main2 leading-[1.5]">{currentResult().description}</p>
              <div class={`${currentResult().bgColor} rounded p-3 w-full`}>
                <p class="font-semibold text-sm text-neutral-main1 leading-[1.5]">{currentResult().recommendation}</p>
              </div>
            </div>
            <div class="flex gap-2 w-full flex-wrap">
              <Button
                type={riskLevel() === 'high' ? 'brand' : 'second'}
                class="flex-1 min-w-[200px] h-11 rounded-full"
                onClick={handlePrimaryAction}
              >
                {currentResult().primaryButton}
              </Button>
              <Button type="second" class="flex-1 min-w-[200px] h-11 rounded-full" onClick={handleTestAgain}>
                {t('Retake Assessment')}
              </Button>
            </div>
          </div>
        </div>
      </Show>
    </div>
  );
}
